import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { PreviewAiDescriptionComponent } from '../preview.ai.description/preview.ai.description.component';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { localStorageKeys } from 'src/app/common/constants/local.storage.keys';
import { TokenInfo } from 'src/app/common/services/auth/auth.service';
import jwt_decode from 'jwt-decode';
import { VisitorEvent } from '../../../models/visitor.event';
import { VisitorPlace } from '../../../models/visitor.place';
import { VisitorService } from '../../../services/visitor.service';
import { SharedModule } from '../../../shared.module';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'ig-ai-description-generator',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './ai.description.generator.component.html',
  styleUrl: './ai.description.generator.component.scss',
})
export class AiDescriptionGeneratorComponent {
  aiGeneratedDescs: string[] = [];
  isGenerating = false;

  @Input() hubName: string;
  @Input() item: VisitorEvent | VisitorPlace;

  modalService = inject(ModalService);
  dashboardService = inject(DashboardService);
  visitorService = inject(VisitorService);

  @Output() onDescriptionSelected = new EventEmitter<string>();

  onGenerateDescription() {
    if (this.isGenerating) return;

    this.isGenerating = true;
    this.dashboardService
      .aiAssistant(this.hubName, {
        query: this.getItemInfoForGeneratingDescription(),
        model: 'chatgpt',
        feature: this.visitorService.getItemType(this.item) + '-description',
      })
      .subscribe((data: { success: boolean; model: string; data: string }) => {
        if (data.success) {
          try {
            const json = JSON.parse(data.data);
            this.aiGeneratedDescs = [json.version1, json.version2, json.version3];
            console.log(this.aiGeneratedDescs);
            this.isGenerating = false;

            const modal = this.modalService.show({
              component: PreviewAiDescriptionComponent,
              data: this.aiGeneratedDescs,
              panelClass: ['!w-[900px]'],
            });
            modal.afterClosed().subscribe((desc) => {
              this.onDescriptionSelected.emit(desc);
            });

            this.trackItemAIContentAssistant();
          } catch (err) {
            this.isGenerating = false;
            console.error(err);
          }
        }
      });
  }

  showPreview() {
    const modal = this.modalService.show({
      component: PreviewAiDescriptionComponent,
      data: this.aiGeneratedDescs,
      panelClass: ['!w-[900px]'],
    });
    modal.afterClosed().subscribe((desc) => {
      this.onDescriptionSelected.emit(desc);
    });
  }

  private trackItemAIContentAssistant() {
    if (!window.location.href.includes('localhost')) {
      const authToken = localStorage.getItem(localStorageKeys.authToken);
      let tokenInfo = jwt_decode<TokenInfo>(authToken);
      try {
        let trackName = `click - ${this.visitorService.getItemType(
          this.item
        )} - AIContentAssistant - ${this.item.name}`;
        window['pendo'].track(trackName, {
          accountId: tokenInfo.userName,
          timestamp: Date.now(),
          hubName: this.hubName,
        });
      } catch (error) {
        console.error(error);
      }
    }
  }

  private getItemInfoForGeneratingDescription() {
    if (this.visitorService.getItemType(this.item) === 'place') {
      return `place name: ${this.item.name}; address: ${
        this.item['address']?.address || this.item['address']
      }`;
    }
    return `event title: ${this.item.name}; address: ${
      this.item['address'] ? this.item['address'].address : ''
    }; venue/host: ${
      this.item['venue'] ||
      (this.item['venueInfo'] ? this.item['venueInfo'].name : this.item['owner'].name)
    }`;
  }

  get isPlace() {
    return this.visitorService.getItemType(this.item) === 'place';
  }

  bulkGenerateDescriptions() {
    DashboardService.info$.next({
      type: 'BULK_GENERATE_DESCRIPTIONS',
      data: {
        hubName: this.hubName,
        itemIds: [],
        itemType: this.isPlace ? 'place' : 'event',
      },
    });
  }
}
